import {useEffect, useRef, useState} from 'react';
import {Link, useLocation, useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {usePost, usePostMutations} from '../api/posts';
import ErrorBox from '../components/feedback/ErrorBox';
import Sheet from '../components/feedback/Sheet';
import RemindSheet from '../components/todo/RemindSheet';
import {SkeletonBlock} from '../components/feedback/Skeleton';
import Icon from '../components/Icon';
import KindPill from '../components/tell/KindPill';
import KindTile from '../components/tell/KindTile';
import PhotoPager, {PagerPhoto} from '../components/tell/PhotoPager';
import {uploadQueue, useUploadState} from '../lib/uploadQueue';
import {useAuth} from '../hooks/useAuth';
import {toast} from '../hooks/useToast';
import {familyClasses, kindInfo} from '../lib/kinds';
import {composeShareCard} from '../lib/shareCard';
import {formatDateShort, formatTime} from '../lib/when';
import {Post} from '../types';
import Button from '../components/Button';
import {confirm} from '../hooks/useConfirm';

// One tell (design/screens/TellDetail.dc.html): the photo fills the top, the content sheet
// overlaps it by 28px, then fact rows, Share, and a quiet footer with Delete.

const bodyWithoutTitle = (post: Post): string => {
    const lines = post.text.split('\n');
    const i = lines.findIndex(l => l.trim() !== '');
    return i < 0 ? '' : lines.slice(i + 1).join('\n').trim();
};

const shareText = (post: Post): string =>
    [post.title || kindInfo(post.kind).label, bodyWithoutTitle(post), [formatDateShort(post.whenLocal), post.plants.map(t => t.label).join(', '), post.place?.name].filter(Boolean).join(' · '), '— Sow and Tell']
        .filter(Boolean)
        .join('\n');

const safeFileName = (post: Post, index: number): string => {
    const base = (post.title || kindInfo(post.kind).label)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 40);
    return `${base || 'tell'}${post.photos.length > 1 ? `-${index + 1}` : ''}.jpg`;
};

const metaForCard = (post: Post): string => [formatDateShort(post.whenLocal), post.plants.map(t => t.label).join(', '), post.place?.name].filter(Boolean).join(' · ');

/**
 * Sharing. Phone share sheets drop the text when a file is attached (WhatsApp keeps only the
 * image), so there are four ways out, chosen in a small sheet: the share card (the first photo
 * with the words written on it), just the photos, just the words, or copy the words. Files are
 * fetched and the card composed when the page opens, because iOS allows the share call only
 * inside the tap itself. Sharing any image also copies the caption, ready to paste.
 */
function useShareTell(post: Post | undefined) {
    const {authedFetch} = useAuth();
    const files = useRef<File[]>([]);
    const card = useRef<File | null>(null);
    const [ready, setReady] = useState(false);
    const [cardPreview, setCardPreview] = useState<string | null>(null);
    const photoKey = post?.photos.map(p => p.id).join(',') ?? '';
    const canShareFiles = typeof navigator.canShare === 'function';

    useEffect(() => {
        files.current = [];
        card.current = null;
        setReady(false);
        setCardPreview(prev => {
            if (prev) URL.revokeObjectURL(prev);
            return null;
        });
        if (!post || post.photos.length === 0 || !canShareFiles) return;
        let cancelled = false;
        void (async () => {
            try {
                const fetched = await Promise.all(
                    post.photos.map(async (photo, i) => {
                        const response = await authedFetch(photo.url);
                        if (!response.ok) throw new Error(`photo ${response.status}`);
                        const blob = await response.blob();
                        return new File([blob], safeFileName(post, i), {type: blob.type || 'image/jpeg'});
                    })
                );
                if (cancelled) return;
                files.current = fetched;
                try {
                    card.current = await composeShareCard(fetched[0], {title: post.title || kindInfo(post.kind).label, meta: metaForCard(post)}, safeFileName(post, 0).replace(/\.jpg$/, '-card.jpg'));
                    if (!cancelled) setCardPreview(URL.createObjectURL(card.current));
                } catch {
                    card.current = null; // the plain photos still share
                }
                if (!cancelled) setReady(true);
            } catch {
                // The words still share; the photos just will not ride along this time.
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [post?.id, photoKey, authedFetch, canShareFiles]);

    const copyWords = async (quiet = false) => {
        if (!post) return false;
        try {
            await navigator.clipboard.writeText(shareText(post));
            if (!quiet) toast.success('Copied the words. Paste them wherever you like.');
            return true;
        } catch {
            if (!quiet) toast.problem("Couldn't copy that.");
            return false;
        }
    };

    const shareFiles = async (chosen: File[]) => {
        if (!post) return;
        const title = post.title || undefined;
        try {
            if (chosen.length > 0 && navigator.canShare?.({files: chosen})) {
                const copied = await copyWords(true).catch(() => false);
                await navigator.share({files: chosen, title, text: shareText(post)});
                if (copied) toast.info('The words are on your clipboard too, in case the app kept only the photo.');
                return;
            }
            await shareWords();
        } catch (error) {
            if ((error as Error)?.name !== 'AbortError') toast.problem("Couldn't share that.");
        }
    };

    const shareWords = async () => {
        if (!post) return;
        try {
            if (navigator.share) await navigator.share({title: post.title || undefined, text: shareText(post)});
            else await copyWords();
        } catch (error) {
            if ((error as Error)?.name !== 'AbortError') toast.problem("Couldn't share that.");
        }
    };

    return {
        ready,
        cardPreview,
        hasPhotos: (post?.photos.length ?? 0) > 0 && canShareFiles,
        hasCard: () => card.current !== null,
        shareCard: () => shareFiles(card.current ? [card.current] : files.current),
        sharePhotos: () => shareFiles(files.current),
        shareWords,
        copyWords: () => void copyWords()
    };
}

/** Delete with a confirm first, the button busy while it happens, then the Journal with an Undo. */
export function useDeleteTell() {
    const {softDelete, restore} = usePostMutations();
    const navigate = useNavigate();
    return async (post: Post) => {
        const done = await confirm({
            title: `Delete “${post.title || kindInfo(post.kind).label}”?`,
            body: 'It goes to Recently deleted, out of the Journal and the filters.',
            help: 'You can bring it back from More → Recently deleted for 30 days. After that it is gone, photos and all.',
            confirmLabel: 'Delete',
            busyLabel: 'Deleting…',
            destructive: true,
            action: () => softDelete.mutateAsync(post.id)
        });
        if (done) {
            navigate('/journal', {replace: true});
            toast.info('Tell deleted.', {action: {label: 'Undo', onClick: () => restore.mutate(post.id)}});
        }
    };
}

export default function TellDetailPage() {
    const {id} = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const post = usePost(id);
    const {restore, patch} = usePostMutations();
    const deleteTell = useDeleteTell();
    const upload = useUploadState(id);
    const sharing = useShareTell(post.data);
    const [params] = useSearchParams();
    const [shareOpen, setShareOpen] = useState(params.get('share') === '1');
    const [reminding, setReminding] = useState(false);

    if (post.isError) {
        return (
            <div className="flex flex-col gap-4 pt-16">
                <ErrorBox error={post.error} onRetry={() => void post.refetch()} />
                <button type="button" className="btn btn-secondary" onClick={() => navigate('/journal')}>
                    Back to the Journal
                </button>
            </div>
        );
    }
    if (!post.data) {
        return (
            <div className="-mx-4 flex flex-col gap-4">
                <SkeletonBlock className="h-[240px] rounded-none" />
                <div className="flex flex-col gap-3 px-4">
                    <SkeletonBlock className="h-6 w-1/3" />
                    <SkeletonBlock className="h-8 w-3/4" />
                    <SkeletonBlock className="h-4 w-full" />
                </div>
            </div>
        );
    }

    const p = post.data;
    const pending = upload?.photos.filter(q => q.status !== 'done') ?? [];
    const pagerPhotos: PagerPhoto[] = [
        ...p.photos.map(ph => ({key: ph.id, url: ph.url})),
        ...pending.map(q => ({key: q.id, url: q.previewUrl, uploading: q.status !== 'failed', failed: q.status === 'failed'}))
    ];
    const cover = pagerPhotos.length > 0;
    const info = kindInfo(p.kind);
    const body = bodyWithoutTitle(p);
    // location.key is 'default' only on the first page of the visit (opened from a link or a
    // reload); history.length counts other sites' pages too, so it cannot tell.
    const back = () => (location.key !== 'default' ? navigate(-1) : navigate('/journal', {replace: true}));

    return (
        <div className="settle-in -mx-4 -mt-[max(12px,env(safe-area-inset-top))] flex min-h-dvh flex-col">
            <div className={`relative ${cover ? 'bg-sunken' : `h-[200px] ${familyClasses(info.family)}`}`}>
                {cover ? <PhotoPager photos={pagerPhotos} ratio={p.photos[0] ? p.photos[0].width / p.photos[0].height : undefined} /> : <KindTile kind={p.kind} size={72} className="absolute bottom-14 left-4" />}
                {/* Themed chips, not a see-through scrim: they read the same on any photo. */}
                <button type="button" onClick={back} aria-label="Back" className="absolute left-3 top-[max(16px,env(safe-area-inset-top))] flex h-11 w-11 items-center justify-center rounded-pill bg-surface/90 text-ink shadow-1 backdrop-blur-md">
                    <Icon name="back" size={22} />
                </button>
                {!p.deletedAt && (
                    <button type="button" onClick={() => navigate(`/tell/${p.id}/edit`, {state: {fromTell: true}})} className="absolute right-3 top-[max(16px,env(safe-area-inset-top))] flex h-11 items-center gap-2 rounded-pill bg-surface/90 px-4 text-[15px] font-[650] text-ink shadow-1 backdrop-blur-md">
                        <Icon name="edit" size={18} /> Edit
                    </button>
                )}
            </div>

            {/* relative so the sheet paints over the (positioned) photo it overlaps. */}
            <div className="relative -mt-7 flex flex-1 flex-col gap-[18px] rounded-t-[26px] bg-ground px-4 pb-8 pt-[22px]">
                {upload?.failed && (
                    <div className="problem flex items-center gap-3">
                        <span className="flex-1">A photo did not upload: {upload.photos.find(q => q.status === 'failed')?.error}</span>
                        <button type="button" className="font-display font-bold underline-offset-2 hover:underline" onClick={() => uploadQueue.retry(p.id)}>
                            Retry
                        </button>
                    </div>
                )}
                {p.deletedAt && (
                    <div className="nudge flex items-center gap-3">
                        <span className="flex-1">This tell is in Recently deleted.</span>
                        <button type="button" className="font-display font-bold underline-offset-2 hover:underline" onClick={() => restore.mutate(p.id, {onSuccess: () => toast.success('Restored.')})}>
                            Restore
                        </button>
                    </div>
                )}
                <div className="flex flex-col gap-[10px]">
                    <div className="meta flex items-center gap-2">
                        <KindPill kind={p.kind} size="md" />
                        <span>
                            {p.detail ? `${p.detail} · ` : ''}
                            {formatDateShort(p.whenLocal)} · {formatTime(p.whenLocal)}
                        </span>
                    </div>
                    <h1 className="title m-0">{p.title || info.label}</h1>
                    {body && <p className="m-0 whitespace-pre-wrap text-ink-2">{body}</p>}
                </div>

                {(p.plants.length > 0 || p.place || p.quantity) && (
                    <div className="rows rounded-[18px] border-[1.5px] border-line bg-surface">
                        {p.plants.map(plant => (
                            <Link key={plant.id} to={`/plants/${plant.id}`} className="flex min-h-[58px] items-center gap-3 px-[14px] py-2 no-underline">
                                <Icon name="leaf" size={20} className="text-shoot-text" />
                                <span className="flex-1">
                                    <span className="block font-[650]">{plant.label}</span>
                                    <span className="meta block">Every season of this plant</span>
                                </span>
                                <Icon name="chevron" size={20} className="text-ink-3" />
                            </Link>
                        ))}
                        {p.place && (
                            <Link to={`/places/${p.place.id}`} className="flex min-h-[58px] items-center gap-3 px-[14px] py-2 no-underline">
                                <Icon name="pin" size={20} className="text-shoot-text" />
                                <span className="flex-1">
                                    <span className="block font-[650]">{p.place.name}</span>
                                    <span className="meta block">{p.place.area ? `${p.place.area.name} · everything here` : 'Everything here'}</span>
                                </span>
                                <Icon name="chevron" size={20} className="text-ink-3" />
                            </Link>
                        )}
                        {p.quantity && (
                            <div className="flex min-h-[52px] items-center gap-3 px-[14px]">
                                <Icon name="hash" size={20} className="text-ink-2" />
                                <span className="font-[650]">{p.quantity}</span>
                            </div>
                        )}
                    </div>
                )}

                {!p.deletedAt && (
                    <div className="grid grid-cols-[1fr_1fr_auto] gap-[10px]">
                        <Button className="rounded-[16px]" onClick={() => (sharing.hasPhotos ? setShareOpen(true) : sharing.shareWords())}>
                            <Icon name="share" size={20} /> Share
                        </Button>
                        <button type="button" className="btn btn-secondary rounded-[16px]" onClick={() => setReminding(true)}>
                            <Icon name="reminder" size={20} className="text-marigold-text" /> Remind me
                        </button>
                        {/* Makes me happy: a flag on any kind, in marigold, the "something good happened" colour. */}
                        <Button
                            variant="secondary"
                            aria-pressed={p.happy}
                            aria-label={p.happy ? 'Makes me happy (tap to unmark)' : 'Makes me happy'}
                            title="Makes me happy"
                            className={`w-[52px] rounded-[16px] px-0 ${p.happy ? 'border-marigold bg-marigold-soft text-marigold-text' : 'text-ink-2'}`}
                            busy={patch.isPending}
                            onClick={() => patch.mutateAsync({id: p.id, happy: !p.happy}).then(post => toast.success(post.happy ? 'Marked: makes you happy.' : 'Unmarked.'))}
                        >
                            <Icon name="happy" size={22} />
                        </Button>
                    </div>
                )}

                <Sheet open={shareOpen} onClose={() => setShareOpen(false)} title="Share this tell">
                    <div className="flex flex-col gap-2">
                        <p className="m-0 text-sm text-ink-2">Most apps keep only the photo and drop the words, so pick how it travels.</p>
                        {sharing.cardPreview && <img src={sharing.cardPreview} alt="The photo with the words on it" className="w-full rounded-md border border-line" />}
                        <Button busy={!sharing.ready} busyLabel="Preparing the photo…" onClick={() => { setShareOpen(false); return sharing.shareCard(); }}>
                            <Icon name="camera" size={20} /> Photo with the words on it
                        </Button>
                        <Button variant="secondary" disabled={!sharing.ready} onClick={() => { setShareOpen(false); return sharing.sharePhotos(); }}>
                            Just the {p.photos.length > 1 ? 'photos' : 'photo'}
                        </Button>
                        <Button variant="secondary" onClick={() => { setShareOpen(false); return sharing.shareWords(); }}>
                            Just the words
                        </Button>
                        <Button variant="secondary" onClick={() => { setShareOpen(false); sharing.copyWords(); }}>
                            Copy the words
                        </Button>
                    </div>
                </Sheet>

                {reminding && <RemindSheet post={p} onClose={() => setReminding(false)} />}

                <footer className="mt-auto flex items-center justify-between pt-4 text-xs text-ink-2">
                    <span className="flex items-center gap-2">
                        <Icon name="lock" size={16} /> Private · only you can see this
                    </span>
                    {!p.deletedAt && (
                        <Button variant="ghost" danger size="sm" className="px-2" onClick={() => deleteTell(p)}>
                            Delete
                        </Button>
                    )}
                </footer>
            </div>
        </div>
    );
}
